import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CreditCard, Mail, RefreshCw, Save, Sparkles } from 'lucide-react';
import { commandId, readCkScope, scopeKey, useCkScope } from '../components/CkOperationsShared';
import { createReceivingApi } from './api';
import { formatMoney } from '../utils/money';
import './receiving.css';

export default function Claims({ userId, claimId }) {
  const scope = useCkScope();
  return <Surface key={`${userId}:${claimId || 'list'}:${scopeKey(scope)}`} {...{ userId, claimId, scope }} />;
}

function Surface({ userId, claimId, scope }) {
  const [rows, setRows] = useState([]);
  const [claim, setClaim] = useState(null);
  const [caps, setCaps] = useState({});
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [recipient, setRecipient] = useState('');
  const [financeNote, setFinanceNote] = useState('');
  const [responseType, setResponseType] = useState('acknowledged');
  const [supplierContact, setSupplierContact] = useState('');
  const [responseSummary, setResponseSummary] = useState('');
  const [responseEvidence, setResponseEvidence] = useState('');
  const [operandiReview, setOperandiReview] = useState(null);
  const [creditReconciliation, setCreditReconciliation] = useState(null);
  const [legacyCreditNoteId, setLegacyCreditNoteId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const alive = useRef(false);
  const lock = useRef(false);
  const api = useMemo(() => createReceivingApi(() => ({ ...readCkScope(), userId })), [userId]);
  const active = useCallback(
    () => alive.current && scopeKey(readCkScope()) === scopeKey(scope),
    [scope],
  );
  const savedTextUnchanged = !!claim && subject.trim() === (claim.subject || '').trim()
    && body.trim() === (claim.body || '').trim();
  const disputeOpen = (claim?.supplier_responses || []).reduce((open, response) =>
    response.response_type === 'disputed' ? true
      : response.response_type === 'clarified' ? false : open, false);

  const load = useCallback(async () => {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    setError('');
    try {
      const capability = await api.capabilities();
      if (claimId) {
        const row = await api.claim(claimId);
        if (!active()) return;
        setClaim(row);
        setSubject(row.subject || '');
        setBody(row.body || '');
        setRecipient(row.recipient || '');
        setOperandiReview(null);
        setCreditReconciliation(null);
        setLegacyCreditNoteId('');
      } else {
        const list = await api.claims();
        if (!active()) return;
        setRows(list);
      }
      setCaps(capability);
    } catch (e) {
      if (active()) setError(e.message);
    } finally {
      lock.current = false;
      if (active()) setBusy(false);
    }
  }, [active, api, claimId]);

  useEffect(() => {
    alive.current = true;
    load();
    return () => { alive.current = false; };
  }, [load]);

  async function act(kind) {
    if (!claim || lock.current || !caps.can_manage) return;
    if (kind === 'send' && !window.confirm(`Send this claim to ${recipient}?`)) return;
    if (kind === 'credit' && !window.confirm('Create a draft supplier credit in Xero with finance approval?')) return;
    lock.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      let next;
      if (kind === 'save') {
        next = await api.saveClaim(claim.id, {
          command_id: commandId(), version: claim.version,
          subject: subject.trim(), body: body.trim(),
        });
      } else if (kind === 'send') {
        next = await api.sendClaim(claim.id, {
          command_id: commandId(), version: claim.version, recipient: recipient.trim(),
        });
      } else if (kind === 'supplier-response') {
        next = await api.recordSupplierResponse(claim.id, {
          command_id: commandId(), version: claim.version, response_type: responseType,
          supplier_contact: supplierContact.trim(), summary: responseSummary.trim(),
          evidence_reference: responseEvidence.trim(),
        });
      } else {
        next = await api.approveClaimCredit(claim.id, {
          command_id: commandId(), version: claim.version, finance_note: financeNote.trim(),
        });
      }
      if (!active()) return;
      setClaim(next);
      if (kind === 'save') setOperandiReview(null);
      setNotice(kind === 'save' ? 'Draft saved.' : kind === 'send'
        ? 'Supplier message recorded by the configured provider.'
        : kind === 'supplier-response'
          ? 'Supplier response recorded as staff-entered and unverified.'
          : 'Finance-approved Xero draft recorded.');
    } catch (e) {
      if (active()) setError(e.message);
    } finally {
      lock.current = false;
      if (active()) setBusy(false);
    }
  }

  async function reviewWithOperandi() {
    if (!claim || lock.current || !caps.can_manage || claim.can_request_operandi_advisory !== true || claim.status !== 'draft' || !savedTextUnchanged) return;
    lock.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    setOperandiReview(null);
    try {
      const result = await api.operandiDryRunClaim(claim.id);
      if (!active()) return;
      if (result.claim_version !== claim.version) throw new Error('Claim version changed; reload before reviewing.');
      setOperandiReview(result.dry_run);
      setNotice(`Operandi reviewed saved claim version ${result.claim_version}. No business action was taken.`);
    } catch (e) {
      if (active()) setError(e.message);
    } finally {
      lock.current = false;
      if (active()) setBusy(false);
    }
  }

  async function checkCreditInXero(creditNoteId = '') {
    if (!claim || lock.current || !caps.can_approve_supplier_credit || claim.status !== 'finance_pending') return;
    lock.current = true;
    setBusy(true);
    setError('');
    setCreditReconciliation(null);
    try {
      const result = await api.reconcileClaimCredit(claim.id, creditNoteId.trim());
      if (!active()) return;
      if (result.claim_version !== claim.version) throw new Error('Claim changed; reload before reconciliation.');
      setCreditReconciliation(result);
      setNotice(result.needs_credit_note_id
        ? 'This legacy posting has no FoodOps credit number. Enter the CreditNoteID shown in the bound Xero organization; the lookup will remain read-only.'
        : result.match_count === 1
        ? 'One exact supplier-credit draft was found by read-only lookup. Review it before confirming.'
        : result.candidate_count !== result.match_count
          ? 'The read-only lookup returned ' + result.candidate_count + ' numbered candidate(s), but none passed all supplier, type, status, currency and total checks.'
          : 'Read-only lookup returned ' + result.match_count + ' eligible drafts; no automatic selection was made.');
    } catch (e) {
      if (active()) setError(e.message);
    } finally {
      lock.current = false;
      if (active()) setBusy(false);
    }
  }

  async function confirmCreditReconciliation() {
    const match = creditReconciliation?.matches?.[0];
    if (!claim || !match || creditReconciliation.match_count !== 1 || lock.current) return;
    if (!window.confirm('Confirm Xero draft ' + match.credit_note_id + ' as the supplier credit for this claim? This records the match in FoodOps and does not write to Xero.')) return;
    lock.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const next = await api.confirmClaimCreditReconciliation(claim.id, {
        command_id: commandId(), version: claim.version,
        credit_note_id: match.credit_note_id, finance_note: financeNote.trim(),
      });
      if (!active()) return;
      setClaim(next);
      setCreditReconciliation(null);
      setNotice('The matching Xero supplier-credit draft was recorded after explicit finance confirmation. No provider write was made.');
    } catch (e) {
      if (active()) setError(e.message);
    } finally {
      lock.current = false;
      if (active()) setBusy(false);
    }
  }

  if (!claimId) return (
    <main className="receiving-queue">
      <header className="receiving-heading"><div><p className="receiving-eyebrow">Receiving</p><h1>Supplier claims</h1></div>
        <button onClick={load} disabled={busy} title="Refresh" aria-label="Refresh"><RefreshCw size={20} /></button>
      </header>
      {error && <p className="receiving-error" role="alert">{error}</p>}
      <ul className="receiving-inbox">{rows.map(row => <li key={row.id}>
        <a href={`/receiving/claims/${row.id}`}>{row.supplier_name || row.supplier_id || 'Supplier'}</a>
        <span>{formatMoney(row.amount)} · {row.delivery_outcome === 'SIMULATED' ? 'simulated message' : row.status} · {row.due_by ? `due ${row.due_by}` : 'awaiting vendor terms'}</span>
      </li>)}</ul>
      {!busy && !rows.length && <p className="receiving-empty">No supplier claims.</p>}
    </main>
  );

  return (
    <main className="receiving-queue">
      <header className="receiving-heading"><div><p className="receiving-eyebrow">Advisory claim</p>
        <h1>{claim?.supplier_name || 'Supplier claim'}</h1></div>
        <button onClick={load} disabled={busy} title="Reload" aria-label="Reload"><RefreshCw size={20} /></button>
      </header>
      <nav className="receiving-links"><a href="/receiving/claims">Claims hub</a></nav>
      {error && <p className="receiving-error" role="alert">{error}</p>}
      {notice && <p role="status">{notice}</p>}
      {claim && <>
        <p>Status: {claim.delivery_outcome === 'SIMULATED' ? 'simulated message' : claim.status} · {formatMoney(claim.amount)} · {claim.due_by ? `due ${claim.due_by}` : 'awaiting vendor terms'}</p>
        {!claim.due_by && <p role="status">No supplier notice deadline is recorded because this vendor declares no discrepancy-notice window. No deadline has been invented.</p>}
        {claim.delivery_outcome === 'SIMULATED' && <p role="status">Simulated message recorded. No supplier email was sent.</p>}
        {claim.delivery_outcome === 'REAL_ACCEPTED_UNVERIFIED' && <p role="status">Message accepted by provider. Delivery is not independently verified.</p>}
        {claim.status === 'send_outcome_unknown' && <p role="alert">Supplier message outcome unknown. Sending is frozen; reconcile with the provider before another send.</p>}
        <p>Decision: {claim.decision?.suggestion}; advisory only. A manager must send and an organization finance approver must approve the Xero draft.</p>
        {(claim.supplier_responses || []).length > 0 && <section className="receiving-arrival" aria-labelledby="supplier-responses-title">
          <h2 id="supplier-responses-title">Supplier correspondence log</h2>
          <p>Staff-entered reports only. These entries do not verify sender identity or establish supplier liability.</p>
          <ol>{claim.supplier_responses.map(response => <li key={response.id}>
            <strong>{response.response_type.replace(/_/g, ' ')}</strong> from {response.supplier_contact}
            <p>{response.summary}</p>
            <small>Reference: {response.evidence_reference} · {response.recorded_at} · NOT VERIFIED</small>
          </li>)}</ol>
        </section>}
        {caps.can_manage && ['sent', 'acknowledged'].includes(claim.status) && <section className="receiving-arrival" aria-labelledby="supplier-response-form-title">
          <h2 id="supplier-response-form-title">Record supplier response</h2>
          <p>Enter a response received outside FoodOps. This form does not contact the supplier or verify the source.</p>
          <label>Response type<select value={responseType} onChange={e => setResponseType(e.target.value)}>
            <option value="acknowledged">Acknowledged receipt of notice</option>
            <option value="disputed">Disputed the claim</option>
            <option value="credit_offered">Credit offered</option>
            <option value="redelivery_offered">Redelivery offered</option>
            <option value="clarified">Clarification resolves a prior dispute</option>
            <option value="other">Other response</option>
          </select></label>
          <label>Supplier contact<input value={supplierContact} maxLength={200} onChange={e => setSupplierContact(e.target.value)} /></label>
          <label>Summary<textarea rows={3} value={responseSummary} maxLength={2000} onChange={e => setResponseSummary(e.target.value)} /></label>
          <label>Evidence reference<input value={responseEvidence} maxLength={240} onChange={e => setResponseEvidence(e.target.value)} /></label>
          <button disabled={busy || !supplierContact.trim() || responseSummary.trim().length < 3 || responseEvidence.trim().length < 3}
            onClick={() => act('supplier-response')}>Record response</button>
        </section>}
        <section className="receiving-arrival">
          <label>Subject<input value={subject} disabled={claim.status !== 'draft'} onChange={e => {
            setSubject(e.target.value); setOperandiReview(null);
          }} /></label>
          <label>Message<textarea rows={12} value={body} disabled={claim.status !== 'draft'} onChange={e => {
            setBody(e.target.value); setOperandiReview(null);
          }} /></label>
          <label>Supplier email<input type="email" value={recipient} disabled={claim.status !== 'draft'} onChange={e => setRecipient(e.target.value)} /></label>
          <div className="receiving-actions">
            <button disabled={busy || !caps.can_manage || claim.status !== 'draft' || !subject.trim() || !body.trim()} onClick={() => act('save')}><Save size={18} />Save draft</button>
            <button disabled={busy || !caps.can_manage || claim.status !== 'draft' || !recipient.trim()} onClick={() => act('send')}><Mail size={18} />Send claim</button>
          </div>
        </section>
        {caps.can_manage && claim.can_request_operandi_advisory === true && claim.status === 'draft' && <section className="receiving-arrival" aria-labelledby="operandi-review-title">
          <h2 id="operandi-review-title">Operandi advisory review</h2>
          <p>Reviews the saved claim version only. It cannot edit, send, approve, or execute the claim.</p>
          <button disabled={busy || !savedTextUnchanged} onClick={reviewWithOperandi}>
            <Sparkles size={18} />Review saved draft
          </button>
          {!savedTextUnchanged && <p role="status">Save the edited draft before requesting a review.</p>}
          {operandiReview && <div role="status">
            <h3>Dry-run result: {operandiReview.dry_run?.outcome || 'Unavailable'}</h3>
            <dl>
              <dt>Validation</dt><dd>{operandiReview.dry_run?.level || 'Not reported'}</dd>
              <dt>Real side effect</dt><dd>{operandiReview.dry_run?.real_side_effect_performed === false ? 'None; dry-run only' : 'Not verified'}</dd>
              <dt>Policy</dt><dd>{operandiReview.policy?.outcome || 'Not reported'}</dd>
              <dt>Policy reasons</dt><dd>{Array.isArray(operandiReview.policy?.reason_codes) && operandiReview.policy.reason_codes.length
                ? operandiReview.policy.reason_codes.join(', ') : 'None reported'}</dd>
              <dt>Action hash</dt><dd><code>{operandiReview.intent?.action_hash || 'Not reported'}</code></dd>
            </dl>
          </div>}
        </section>}
        <section className="receiving-arrival">
          <h2>Finance approval</h2>
          <label>Approval note<textarea value={financeNote} onChange={e => setFinanceNote(e.target.value)} /></label>
          {disputeOpen && <p role="alert">Finance approval is blocked until a manager records a clarification resolving the supplier dispute.</p>}
          {claim.status === 'finance_pending' && caps.can_approve_supplier_credit && <>
            <p role="status">An earlier Xero write has an uncertain outcome. Only a read-only lookup is available; no retry will be sent.</p>
            {!['legacy_id_required', 'legacy_id'].includes(creditReconciliation?.lookup_mode) && <button disabled={busy} onClick={() => checkCreditInXero()}>
              <RefreshCw size={18} />Check Xero draft
            </button>}
            {['legacy_id_required', 'legacy_id'].includes(creditReconciliation?.lookup_mode) && <>
              <label>Xero CreditNoteID<input
                value={legacyCreditNoteId}
                maxLength={36}
                placeholder="00000000-0000-0000-0000-000000000000"
                onChange={e => {
                  setLegacyCreditNoteId(e.target.value);
                  setCreditReconciliation(current => current && ({
                    ...current, matches: [], match_count: 0, candidate_count: 0,
                  }));
                }}
              /></label>
              <button disabled={busy || !/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(legacyCreditNoteId)}
                onClick={() => checkCreditInXero(legacyCreditNoteId)}>
                <RefreshCw size={18} />Check this Xero ID
              </button>
            </>}
            {creditReconciliation && <div aria-live="polite">
              {creditReconciliation.matches.map(match => <div key={match.credit_note_id}>
                <p>{match.contact_name || 'Supplier not returned'} · {match.type} · {match.status} · {match.currency || 'currency not returned'} {match.total || 'amount not returned'}</p>
                <p>Credit number: <code>{match.credit_note_number}</code></p>
                <p>Credit note ID: <code>{match.credit_note_id}</code></p>
              </div>)}
              {creditReconciliation.match_count === 1 && <button
                disabled={busy || !financeNote.trim() || !creditReconciliation.matches[0].credit_note_id}
                onClick={confirmCreditReconciliation}>Confirm this Xero match</button>}
            </div>}
          </>}
          <button disabled={busy || !caps.can_approve_supplier_credit || !['sent', 'acknowledged'].includes(claim.status) || disputeOpen || !financeNote.trim()} onClick={() => act('credit')}>
            <CreditCard size={18} />Create Xero supplier credit draft
          </button>
          {claim.xero_credit_note_id && <p>Xero draft: {claim.xero_credit_note_id}{claim.xero_demo_mode ? ' (demo adapter)' : ''}</p>}
        </section>
      </>}
    </main>
  );
}
